/**
 * NPCs react to quests (plan 23 row 44, lane BH).
 *
 * `npcQuestLine` picks the greeting an NPC says to this reader from the lines
 * authored under `[[<npc>.lines]]` in content/npcs/*.toml: every condition on
 * a line must hold (before = neither in the log nor finished, during = in the
 * log, after = finished); the line with the most conditions wins and ties go
 * to the first listed. No match → the NPC's plain `dialogue.greeting`.
 *
 * `npcCompletedQuests` lists the quests this NPC gave or receives that the
 * reader has finished, most recent first, for the "Completed" ribbon.
 *
 * Both read the reader's own quest log (server-projected onto the client), so
 * nothing new crosses the wire.
 */
import { NPC_CONFIG, type NpcQuestLine } from '../content/generated/entities.gen';
import { QUEST_DATABASE } from '../content/generated/quests.gen';

export interface NpcQuestState {
  readonly active: ReadonlyArray<{ readonly id: string }>;
  readonly completed: readonly string[];
}

function lineMatches(line: NpcQuestLine, done: ReadonlySet<string>, active: ReadonlySet<string>): boolean {
  if (line.afterQuest !== undefined && !done.has(line.afterQuest)) return false;
  if (line.duringQuest !== undefined && !active.has(line.duringQuest)) return false;
  if (line.beforeQuest !== undefined && (done.has(line.beforeQuest) || active.has(line.beforeQuest))) return false;
  return true;
}

function specificity(line: NpcQuestLine): number {
  return Number(line.afterQuest !== undefined) + Number(line.duringQuest !== undefined)
    + Number(line.beforeQuest !== undefined);
}

/** The most specific matching line, or null when none matches (or no quest log yet). */
export function pickNpcQuestLine(lines: readonly NpcQuestLine[] | undefined, state: NpcQuestState | undefined): NpcQuestLine | null {
  if (!lines?.length || !state) return null;
  const done = new Set(state.completed);
  const active = new Set(state.active.map(q => q.id));
  let best: NpcQuestLine | null = null;
  for (const line of lines) {
    if (lineMatches(line, done, active) && (!best || specificity(line) > specificity(best))) best = line;
  }
  return best;
}

/** What `npcId` says to this reader: its quest-state line, else `fallback`. */
export function npcQuestGreeting(npcId: string, state: NpcQuestState | undefined, fallback: string): string {
  return pickNpcQuestLine(NPC_CONFIG[npcId]?.lines, state)?.text ?? fallback;
}

export interface NpcCompletedQuest {
  id: string;
  title: string;
}

/**
 * Finished quests this NPC gave (its `start_npc`, or its roster when a quest
 * names none) or receives (`end_npc`), most recent first. Repeatable quests
 * and quests back in the log are left out: they are not done for good.
 */
export function npcCompletedQuests(npcId: string, state: NpcQuestState | undefined): NpcCompletedQuest[] {
  if (!state) return [];
  const roster = new Set(NPC_CONFIG[npcId]?.availableQuests ?? []);
  const active = new Set(state.active.map(q => q.id));
  const seen = new Set<string>();
  const out: NpcCompletedQuest[] = [];
  for (let i = state.completed.length - 1; i >= 0; i--) {
    const id = state.completed[i];
    const def = QUEST_DATABASE[id];
    if (!def || def.repeatable || active.has(id) || seen.has(id)) continue;
    const gave = def.startNpc ? def.startNpc === npcId : roster.has(id);
    if (!gave && def.endNpc !== npcId) continue;
    seen.add(id);
    out.push({ id, title: def.name });
  }
  return out;
}
